import { Formik, Form } from "formik";
import TextField from "../components/common/TextField";
import Button from "../components/common/Button";
import RadioGroup from "../components/common/RadioGroup";
import FormikFieldArray from "../components/common/FormikFieldArray";
import { ReactNode, useMemo } from "react";
import {
  useFetchAllHeadersQuery,
  useFetchAllModifierGroupsQuery,
} from "../store";
import classNames from "classnames";
import {
  FaBoxOpen,
  FaEuroSign,
  FaSortNumericDown,
  FaTags,
  FaLayerGroup,
} from "react-icons/fa";

interface ItemFormProps {
  initialValues: any;
  validationSchema: any;
  onSubmit: (
    values: any,
    formikHelpers: { resetForm: () => void }
  ) => Promise<void>;
  isLoading?: boolean;
  restaurantId: string;
}

const vatOptions = [
  { label: "0%", value: 0 },
  { label: "10%", value: 10 },
  { label: "20%", value: 20 },
];

const ItemForm: React.FC<ItemFormProps> = ({
  initialValues,
  validationSchema,
  onSubmit,
  isLoading,
  restaurantId,
}) => {
  // Fetch available headers & modifier groups
  const { data: headers } = useFetchAllHeadersQuery(restaurantId);
  const { data: modifierGroups } = useFetchAllModifierGroupsQuery(restaurantId);

  const availableHeaders = useMemo(() => {
    if (!headers) return [];
    return headers.map((h: any) => ({ value: h._id, label: h.displayName }));
  }, [headers]);

  const availableModifierGroups = useMemo(() => {
    if (!modifierGroups) return [];
    return modifierGroups.map((mg: any) => ({
      value: mg._id,
      label: mg.displayName,
    }));
  }, [modifierGroups]);

  const errorMessageClasses = classNames(
    "w-full text-sm text-red-700 border-b border-red-700 rounded mt-1"
  );

  return (
    <Formik
      initialValues={{
        ...initialValues,
        vatRatePercentage: initialValues.vatRatePercentage || 10,
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isSubmitting,
        setFieldValue,
      }) => (
        <Form className="max-w-6xl mx-auto w-full pb-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Main Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Info Card */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-2">
                  <FaBoxOpen className="text-primary" />
                  <h3 className="font-bold text-gray-800 text-lg">
                    Informations Produit
                  </h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="displayName"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Nom du produit
                    </label>
                    <TextField
                      id="displayName"
                      value={values.displayName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      placeholder="Ex: Pizza Margherita"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="description"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Description
                    </label>
                    <TextField
                      id="description"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      placeholder="Ingrédients, allergènes..."
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Modifier Groups Card */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-2">
                  <FaLayerGroup className="text-primary" />
                  <h3 className="font-bold text-gray-800 text-lg">
                    Groupes de suppléments
                  </h3>
                </div>

                <FormikFieldArray
                  name="modifierGroups"
                  pushItem={null}
                  renderItem={(mg: any) => (
                    <div className="font-medium text-gray-700">
                      {mg.displayName}
                    </div>
                  )}
                  renderAddButton={(push) => (
                    <div className="relative">
                      <select
                        className="w-full p-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all appearance-none cursor-pointer"
                        onChange={(e) => {
                          const selected = availableModifierGroups.find(
                            (o: any) => o.value === e.target.value
                          );
                          if (selected) {
                            push({
                              _id: selected.value,
                              displayName: selected.label,
                            });
                          }
                          e.target.value = "";
                        }}
                        defaultValue=""
                      >
                        <option value="" disabled>
                          + Ajouter un groupe de suppléments
                        </option>
                        {availableModifierGroups
                          .filter(
                            (opt: any) =>
                              !values.modifierGroups.some(
                                (sel: any) => sel._id === opt.value
                              )
                          )
                          .map((opt: any) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-gray-500">
                        <FaPlusCircleIcon />
                      </div>
                    </div>
                  )}
                />
              </div>
            </div>

            {/* Right Column - Side Settings */}
            <div className="space-y-6">
              {/* Pricing Card */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-2">
                  <FaEuroSign className="text-primary" />
                  <h3 className="font-bold text-gray-800 text-lg">
                    Prix & TVA
                  </h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="price"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Prix (€)
                    </label>
                    <TextField
                      id="price"
                      value={values.price}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      placeholder="0.00"
                      className="w-full font-mono"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Taux de TVA
                    </label>
                    <RadioGroup
                      label=""
                      options={vatOptions}
                      value={values.vatRatePercentage}
                      onChange={(val) =>
                        setFieldValue("vatRatePercentage", Number(val))
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Categorization Card */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-2">
                  <FaTags className="text-primary" />
                  <h3 className="font-bold text-gray-800 text-lg">
                    Catégories
                  </h3>
                </div>

                <FormikFieldArray
                  name="headers"
                  pushItem={null}
                  renderItem={(h: any) => (
                    <span className="truncate mr-2 font-medium text-gray-700 text-sm">
                      {h.displayName}
                    </span>
                  )}
                  renderAddButton={(push) => (
                    <select
                      className="w-full p-2 border border-gray-300 rounded bg-white text-sm focus:ring-1 focus:ring-primary outline-none"
                      onChange={(e) => {
                        const selected = availableHeaders.find(
                          (o: any) => o.value === e.target.value
                        );
                        if (selected) {
                          push({
                            _id: selected.value,
                            displayName: selected.label,
                          });
                        }
                        e.target.value = "";
                      }}
                      defaultValue=""
                    >
                      <option value="" disabled>
                        Ajouter une catégorie
                      </option>
                      {availableHeaders
                        .filter(
                          (opt: any) =>
                            !values.headers.some(
                              (sel: any) => sel._id === opt.value
                            )
                        )
                        .map((opt: any) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                    </select>
                  )}
                />
              </div>

              {/* Display Settings Card */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-2">
                  <FaSortNumericDown className="text-primary" />
                  <h3 className="font-bold text-gray-800 text-lg">Affichage</h3>
                </div>

                <div>
                  <label
                    htmlFor="displayOrder"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Ordre d&apos;affichage
                  </label>
                  <TextField
                    id="displayOrder"
                    value={values.displayOrder}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errors={errors}
                    touched={touched}
                    placeholder="0"
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end border-t border-gray-200 pt-6">
            <Button
              className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-lg font-bold shadow-md transition-all transform hover:scale-[1.02] active:scale-[0.98]"
              disabled={isSubmitting || isLoading}
              type="submit"
            >
              {isLoading ? "Enregistrement..." : "Enregistrer le produit"}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

// Helper icon
const FaPlusCircleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

export default ItemForm;
